import React, { useEffect, useMemo, useState } from "react";
import { Box, Paper, Toolbar, Typography, Stack, Button, Snackbar } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";

function StudentResult() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [student, setStudent] = useState(undefined); // undefined = not fetched, null = not found
  const [studentLoading, setStudentLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [subjectDetails, setSubjectDetails] = useState([]);
  const [sgpa, setSgpa] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const user = useMemo(() => {
    try {
      const storedUser = sessionStorage.getItem("user");
      return storedUser ? JSON.parse(storedUser) : null;
    } catch (err) {
      return null;
    }
  }, []);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const showSnackbar = (msg, sev = "success") => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(sev);
    setSnackbarOpen(true);
  };

  function getMonthName(month) {
    const months = [
      "",
      "JANUARY",
      "FEBRUARY",
      "MARCH",
      "APRIL",
      "MAY",
      "JUNE",
      "JULY",
      "AUGUST",
      "SEPTEMBER",
      "OCTOBER",
      "NOVEMBER",
      "DECEMBER",
    ];
    return months[month] || month;
  }

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const formatMonthYear = (month, year) => {
    if (!month) return "";
    const monthText = String(getMonthName(month));
    const yearText = year ? String(year) : "";
    return yearText && !monthText.includes(yearText) ? `${monthText}-${yearText}` : monthText;
  };

  const columns = [
    {
      field: "id",
      headerName: "S.No",
      width: 80,
      valueGetter: (params) => params.api.getRowIndex(params.id) + 1,
    },
    {
      field: "year_number",
      headerName: "StudyYear",
      width: 110,
    },
    {
      field: "semester_number",
      headerName: "Semester",
      width: 110,
    },
    {
      field: "exam_type_name",
      headerName: "ExamType",
      width: 120,
    },
    {
      field: "month_year",
      headerName: "MonthYear",
      width: 120,
      valueGetter: (params) => formatMonthYear(params.row.passed_month, params.row.passed_year),
    },
    {
      field: "is_published",
      headerName: "Status",
      width: 110,
      valueGetter: (params) => (params.row.is_published ? "Published" : "Unpublished"),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="primary"
          size="medium"
          onClick={() => handleShowResult(params.row)}
          sx={{
            color: "#fff",
            "&:hover": { color: "#fff" },
            "& .MuiButton-startIcon, & .MuiButton-endIcon": { color: "#fff" },
          }}
        >
          Show
        </Button>
      ),
    },
  ];

  useEffect(() => {
    if (!user?.client_id || !user?.user_email) {
      setStudent(null);
      setStudentLoading(false);
      setLoading(false);
      return;
    }
    setStudentLoading(true);
    const fetchStudent = async () => {
      try {
        const url = `${API_BASE_URL}/api/students/by-email/${user.client_id}/${encodeURIComponent(
          user.user_email
        )}?college_id=${encodeURIComponent(user.college_id || "")}`;
        const res = await axios.get(url, { headers: getAuthHeaders() });
        const data = res.data?.data || res.data || null;
        if (data && data.student_id) {
          setStudent(data);
        } else {
          setStudent(null);
        }
      } catch (err) {
        setStudent(null);
      } finally {
        setStudentLoading(false);
      }
    };
    fetchStudent();
  }, [user?.client_id, user?.user_email, API_BASE_URL]);

  useEffect(() => {
    if (student === null) {
      setLoading(false);
      setResults([]);
      return;
    }
    if (!student || !user?.client_id) return;

    const fetchResults = async () => {
      setLoading(true);
      try {
        const url = `${API_BASE_URL}/api/studentresulthistory/student-final-results?student_id=${student.student_id}&client_id=${user.client_id}`;
        const res = await axios.get(url, { headers: getAuthHeaders() });
        setResults(res.data?.data || []);
      } catch (err) {
        setResults([]);
        showSnackbar("Error fetching result history", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchResults();
  }, [student, user?.client_id, API_BASE_URL]);

  function handleShowResult(resultRow) {
    setSelectedResult(resultRow);
    setModalOpen(true);
    setSubjectDetails([]);
    setSgpa(null);
    (async () => {
      try {
        const params = new URLSearchParams({
          student_id: student.student_id,
          year_number: resultRow.year_number || "",
          semester_number: resultRow.semester_number || "",
          client_id: user.client_id,
          exam_type_name: resultRow.exam_type_name || "",
          passed_month: resultRow.passed_month || "",
          passed_year: resultRow.passed_year || "",
        });
        const url = `${API_BASE_URL}/api/studentresulthistory/subjects-by-student-year-semester?${params.toString()}`;
        const res = await axios.get(url, { headers: getAuthHeaders() });
        setSubjectDetails(res.data?.data || []);
      } catch (err) {
        setSubjectDetails([]);
        setSgpa(null);
        showSnackbar("Error fetching result details", "error");
      }
    })();
  }

  const rows = Array.from(
    results
      .reduce((acc, row) => {
        const key = [
          row.year_number,
          row.semester_number,
          row.exam_type_name,
          row.passed_month,
          row.passed_year,
        ].join("|");
        if (!acc.has(key)) {
          acc.set(key, {
            ...row,
            id: row.result_id || key,
          });
        }
        return acc;
      }, new Map())
      .values()
  );

  return (
    <Box p={2} sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
      <Paper
        elevation={1}
        sx={{
          width: "100%",

          margin: "0 auto",
          overflowX: "auto",
          boxSizing: "border-box",
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            STUDENT RESULT HISTORY
          </Typography>
        </Toolbar>
        <Box p={2} sx={{ width: "100%", boxSizing: "border-box" }}>
          <Stack spacing={2}>
            {studentLoading ? (
              <Typography variant="h6">Loading student info...</Typography>
            ) : student === null ? (
              <Typography variant="h6" color="error">
                Student Details are not found.
              </Typography>
            ) : (
              <>
                <DataGrid
                  rows={rows}
                  columns={columns}
                  autoHeight
                  loading={loading}
                  disableRowSelectionOnClick
                  hideFooter
                  sx={{ width: "auto", minWidth: 900 }}
                />
                <Typography variant="subtitle2" sx={{ mt: 2 }}>
                  <b>Note:</b> In case of any deviation, the concern students may contact the
                  COE(Examination Cell) immediately.
                </Typography>
              </>
            )}
          </Stack>
        </Box>
      </Paper>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, fontSize: 20, pb: 1 }}>EXAM RESULTS</DialogTitle>
        <DialogContent>
          {selectedResult && (
            <Box sx={{ border: "1px solid #bbb", borderRadius: 1, p: 2, background: "#fff" }}>
              <Typography
                variant="subtitle1"
                sx={{ mb: 2, fontWeight: 600, borderBottom: "1px solid #bbb", pb: 1 }}
              >
                {`${selectedResult.year_number} YEAR ${selectedResult.semester_number} SEMESTER ${
                  selectedResult.exam_type_name?.toUpperCase() || ""
                } EXAMINATIONS, ${formatMonthYear(
                  selectedResult.passed_month,
                  selectedResult.passed_year
                )?.toUpperCase()}`}
              </Typography>

              <DataGrid
                rows={subjectDetails.map((row, idx) => ({
                  ...row,
                  id: idx + 1,
                  subject_name:
                    typeof row.subject_name === "object"
                      ? Object.values(row.subject_name)[0] || ""
                      : row.subject_name || "",
                  credits:
                    typeof row.credits === "object"
                      ? Object.values(row.credits)[0] || ""
                      : row.credits || "",
                  grade:
                    typeof row.grade === "object"
                      ? Object.values(row.grade)[0] || ""
                      : row.grade || "",
                  grade_point:
                    typeof row.grade_point === "object"
                      ? Object.values(row.grade_point)[0] || ""
                      : row.grade_point !== undefined
                      ? row.grade_point
                      : "",
                  credit_points:
                    typeof row.credit_points === "object"
                      ? Object.values(row.credit_points)[0] || ""
                      : row.credit_points || "",
                  status:
                    typeof row.status === "object"
                      ? Object.values(row.status)[0] || ""
                      : row.status || "",
                }))}
                columns={[
                  {
                    field: "sno",
                    headerName: "S.No",
                    width: 80,
                    valueGetter: (params) => params.api.getRowIndex(params.id) + 1,
                  },
                  { field: "subject_name", headerName: "Subject Name", flex: 1.5, minWidth: 180 },
                  {
                    field: "credits",
                    headerName: "Credits",
                    flex: 0.7,
                    minWidth: 80,
                    align: "center",
                    headerAlign: "center",
                  },
                  {
                    field: "grade",
                    headerName: "Grade",
                    flex: 0.7,
                    minWidth: 80,
                    align: "center",
                    headerAlign: "center",
                  },
                  {
                    field: "grade_point",
                    headerName: "Grade Points",
                    flex: 0.9,
                    minWidth: 100,
                    align: "center",
                    headerAlign: "center",
                    renderCell: (params) => (
                      <span>
                        {params.value !== undefined && params.value !== null
                          ? String(params.value)
                          : ""}
                      </span>
                    ),
                  },
                  {
                    field: "credit_points",
                    headerName: "Credit Points",
                    flex: 0.9,
                    minWidth: 100,
                    align: "center",
                    headerAlign: "center",
                  },
                  {
                    field: "status",
                    headerName: "Status",
                    flex: 0.8,
                    minWidth: 90,
                    align: "center",
                    headerAlign: "center",
                    renderCell: (params) => (
                      <span
                        style={{
                          color:
                            params.value === "PASS"
                              ? "green"
                              : params.value === "FAIL"
                              ? "red"
                              : undefined,
                          fontWeight: 600,
                        }}
                      >
                        {params.value}
                      </span>
                    ),
                  },
                ]}
                autoHeight
                hideFooter
                disableRowSelectionOnClick
                sx={{ border: "1px solid #bbb", borderRadius: 1, background: "#fff", mt: 1 }}
              />
              {subjectDetails.length === 0 && (
                <Typography variant="body2" sx={{ mt: 2 }}>
                  No subject details found for this result.
                </Typography>
              )}
              {sgpa && (
                <Typography variant="h6" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>
                  SGPA: {sgpa}
                </Typography>
              )}
              <Box
                sx={{
                  border: "1px solid #bbb",
                  borderRadius: 1,
                  mt: 2,
                  p: 2,
                  background: "#fafafa",
                }}
              >
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <b>Note:</b> In case of any deviation, the concern students may contact the
                  COE(Examination Cell) immediately.
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Letter Grade Indicates Grade Points:
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setModalOpen(false)}
            color="error"
            variant="contained"
            sx={{ color: "#fff", "&:hover": { color: "#fff" } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default StudentResult;
